import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const comparisons = [
  { feature: "AI Lead Generation", aiveliq: true, competitor: false },
  { feature: "AI Sales Engagement", aiveliq: true, competitor: false },
  { feature: "Objection Handling", aiveliq: true, competitor: "partial" },
  { feature: "Workflow Automation", aiveliq: true, competitor: false },
  { feature: "Forecasting", aiveliq: true, competitor: true },
  { feature: "Real-Time Coaching", aiveliq: true, competitor: "partial" },
  { feature: "Flexible Pricing", aiveliq: true, competitor: false },
];

export default function VsGong() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Aiveliq360 vs Gong | AI Sales OS vs Revenue Intelligence"
        description="Compare Aiveliq360 and Gong. Discover why the 360° AI Sales Operating System outperforms traditional revenue intelligence tools in automation and revenue execution."
      />

      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" /></Link>
          <Link to="/auth"><Button size="sm" className="gap-1.5">Get Started<ArrowRight className="h-3.5 w-3.5" /></Button></Link>
        </div>
      </header>

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold mb-6">
            Aiveliq360 vs Gong:{" "}
            <span className="gradient-text">Which Platform Powers a Full Revenue Engine?</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
            Gong is a conversation analytics and revenue intelligence tool. 
            Aiveliq360 is a complete AI Sales Operating System that automates your entire GTM motion.
          </p>

          {/* Comparison Table */}
          <div className="glass rounded-xl overflow-hidden mb-12">
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 font-semibold">
              <div className="text-left">Feature</div>
              <div>Aiveliq360</div>
              <div>Gong</div>
            </div>
            {comparisons.map((row) => (
              <div key={row.feature} className="grid grid-cols-3 gap-4 p-4 border-t border-border">
                <div className="text-left text-sm">{row.feature}</div>
                <div>
                  {row.aiveliq ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  )}
                </div>
                <div>
                  {row.competitor === true ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  ) : row.competitor === "partial" ? (
                    <span className="text-xs text-yellow-500">Partial</span>
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="glass rounded-xl p-6 mb-8">
            <h2 className="font-semibold mb-3">Bottom Line</h2>
            <p className="text-muted-foreground text-sm">
              If you want call analysis only, Gong works. 
              If you want a <strong>full revenue engine that executes</strong>, Aiveliq360 wins decisively.
            </p>
          </div>

          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Switch to Aiveliq360 — The 360° AI Sales OS
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
